import * as React from "react"
import {
  Button,
  Icon,
  Label,
  Grid,
  Image,
  Message,
  Modal,
} from "semantic-ui-react"
import { IFileInfos } from "../../../core/actions"
import { IFileDetails, ISupplierReference } from "../../../models"
import { isError } from "../../../utils"
import { deleteDoublonComplex } from "../../../utils/doublon-tools"
import {
  IChangeFilePositionPayload,
  IDeleteFilePayload,
  IRestoreFilePayload,
} from "../../product-form/_actions"
import { IFieldState } from "../../product-form/_reducers/_utils/product-form-types"

export interface IFileContainerFieldProps {
  error: string
  code: string
  files: IFieldState<IFileDetails[]>
  onDeleteFile: (props: IDeleteFilePayload) => void
  onChangeFilePosition: (props: IChangeFilePositionPayload) => void
}

class FileContainer extends React.Component<IFileContainerFieldProps> {
  // The reducer returns a new list on every file change: comparing references avoids serializing the files on
  // every render (FE-PERF-F05).
  public shouldComponentUpdate(nextProps: IFileContainerFieldProps) {
    return (
      this.props.files.value !== nextProps.files.value ||
      this.props.error !== nextProps.error
    )
  }

  public render() {
    const { files, onDeleteFile, onChangeFilePosition, code, error } =
      this.props
    return (
      <div className="grid-list-picture">
        <Grid columns={2} style={{ height: "100%", overflow: "auto" }}>
          {getImagesDisplay(files, code, onDeleteFile, onChangeFilePosition)}
        </Grid>
        {isError(error) ? (
          <Message
            className="error-field-message error-textarea-message error-pictures-message"
            negative
            header={error}
          />
        ) : (
          ""
        )}
      </div>
    )
  }
}

const getImagesDisplay = (
  files: IFieldState<IFileDetails[]>,
  code: string,
  onDeleteFile: (props: IDeleteFilePayload) => void,
  onChangeFilePosition: (props: IChangeFilePositionPayload) => void
) => {
  const handleDeleteFile =
    (tile: any) =>
    (e: React.SyntheticEvent): void => {
      e.preventDefault()
      e.stopPropagation()
      onDeleteFile({ code, fileInfos: tile })
    }
  const handleChangeFilePosition =
    (tile: any, increment: boolean) =>
    (e: React.SyntheticEvent): void => {
      e.preventDefault()
      e.stopPropagation()
      onChangeFilePosition({ code, fileName: tile.file.name, increment })
    }
  return files.value.length < 1
    ? null
    : files.value.map((tile: any) => (
        <Picture
          name={tile.file.name}
          code={code}
          isMain={tile.isMain}
          src={tile.blob}
          handleChangeFilePositionUp={handleChangeFilePosition(tile, true)}
          handleChangeFilePositionDown={handleChangeFilePosition(tile, false)}
          handleDeleteFile={handleDeleteFile(tile)}
          key={tile.file.name}
        />
      ))
}

export default FileContainer

export const PicturePreviousVersion = (
  code: string,
  onRestoreFile: (props: IRestoreFilePayload) => void,
  date: string,
  previousVersion: ISupplierReference
) => {
  const handleRestoreFile =
    (tile: IFileInfos) =>
    (e: React.SyntheticEvent): void => {
      e.preventDefault()
      e.stopPropagation()
      onRestoreFile({ code, fileInfos: tile })
    }

  return (
    <Modal
      style={{ width: 610 }}
      trigger={
        <Message
          className="previous-files"
          info
          content={
            <span>
              <b>
                {new Date(date).toLocaleDateString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}{" "}
                {" : "}
                {previousVersion?.pictures?.length}
              </b>{" "}
              <Icon name="picture" />
            </span>
          }
        />
      }
      content={
        <div className="previous-pictures-container">
          <Grid columns={2} style={{ height: "100%", overflow: "auto" }}>
            {deleteDoublonComplex(previousVersion.pictures, "name").map(
              (p, i) => (
                <Picture
                  name={p.name}
                  code={p.name}
                  handleRestoreFile={handleRestoreFile(p)}
                  isMain={i === 0}
                  src={p.path}
                  key={p.name}
                />
              )
            )}
          </Grid>
        </div>
      }
    />
  )
}

interface IPicutreProps {
  name: string
  isMain: boolean
  src: any | string
  code: string
  handleDeleteFile?: (e: React.SyntheticEvent) => void
  handleRestoreFile?: (e: React.SyntheticEvent) => void
  handleChangeFilePositionUp?: (e: React.SyntheticEvent) => void
  handleChangeFilePositionDown?: (e: React.SyntheticEvent) => void
}
const Picture = ({
  name,
  isMain,
  src,
  code,
  handleChangeFilePositionUp,
  handleChangeFilePositionDown,
  handleDeleteFile,
  handleRestoreFile,
}: IPicutreProps) => {
  const className = ["file-tile", isMain ? "main-tile" : ""].join(" ")
  const download = () => window.open(src, "_blank")

  return (
    <Grid.Column key={name}>
      <Image key={name} title={name} className={className}>
        <img src={src} alt={name} />
        <div className="file-controller-div">
          <div className="file-label-div">
            <DeleteButton
              code={code}
              handleDeleteFile={handleDeleteFile}
              handleRestoreFile={handleRestoreFile}
            />
            <Button
              name={code}
              type="button"
              className="file-button"
              disabled={!handleChangeFilePositionUp}
              onClick={handleChangeFilePositionUp}
            >
              <Icon name="chevron up" className="file-button-icon" />
            </Button>
            <Button
              name={code}
              type="button"
              className="file-button"
              disabled={!handleChangeFilePositionDown}
              onClick={handleChangeFilePositionDown}
            >
              <Icon name="chevron down" className="file-button-icon" />
            </Button>
            <Button
              name={code}
              type="button"
              className="file-button download-button"
              onClick={download}
            >
              <Icon name="download" className="file-button-icon" />
            </Button>
          </div>
          <div className="file-label-div">
            <Label className="file-label">{name}</Label>
          </div>
        </div>
      </Image>
    </Grid.Column>
  )
}

const DeleteButton = ({
  code,
  handleDeleteFile,
  handleRestoreFile,
}: {
  code: string
  handleDeleteFile?: (e: any) => void
  handleRestoreFile?: (e: any) => void
}) => (
  <Button
    name={code}
    type="button"
    className="file-button file-button-delete"
    disabled={!handleDeleteFile && !handleRestoreFile}
    onClick={handleDeleteFile || handleRestoreFile}
  >
    <Icon
      name={handleDeleteFile ? "trash" : "recycle"}
      className="file-button-icon"
    />
  </Button>
)
